import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-17a62aa4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "subnet-dual-chart-wrap" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignRankOption } from '@/utils/echarts-option/rank'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import { SubnetStatusService, SubnetStatusItem } from './subnet-status/subnet-status.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'subnet-installed-uninstalled-chart',
  props: {
  hasPermission: {
    type: Boolean,
    default: false,
  },
},
  emits: ["segment-click"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const injector = useReflectiveInjector([SubnetStatusService, ResizeService])
const subnetService = injector.get(SubnetStatusService)
const resizeService = injector.get(ResizeService)
const { t, locale } = useI18n()

const isLoading = ref(false)
const rawList = ref<SubnetStatusItem[]>([])

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

function getCssColorVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

const hasData = computed(() => (rawList.value?.length ?? 0) > 0)

/** 按已安装+未安装总数降序，取 TOP5，与列表接口字段一致 */
const sortedRows = computed<SubnetStatusItem[]>(() => {
  const list = rawList.value || []
  const scored = list
    .map((row) => ({
      row,
      total: (row.installed_clients || 0) + (row.uninstalled_clients || 0),
    }))
    .filter((x) => x.total > 0)
  scored.sort((a, b) => b.total - a.total)
  return scored.slice(0, 5).map((x) => x.row)
})

const chartSeriesData = computed(() => {
  const rows = sortedRows.value
  if (!rows.length) {
    return { names: [] as string[], installed: [] as number[], uninstalled: [] as number[] }
  }
  return {
    names: rows.map((r) => r.subnet_address || '—'),
    installed: rows.map((r) => r.installed_clients || 0),
    uninstalled: rows.map((r) => r.uninstalled_clients || 0),
  }
})

function bindChartClick() {
  if (!chartInstance) return
  chartInstance.off('click')
  chartInstance.on('click', (params: any) => {
    if (params.componentType !== 'series') return
    const row = sortedRows.value[params.dataIndex as number]
    if (!row) return
    const segment: 'installed' | 'uninstalled' = params.seriesIndex === 0 ? 'installed' : 'uninstalled'
    const n = segment === 'installed' ? row.installed_clients || 0 : row.uninstalled_clients || 0
    if (!n) return
    emit('segment-click', { row, segment })
  })
}

function createChart() {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  bindChartClick()
}

function getChartOption() {
  const { names, installed, uninstalled } = chartSeriesData.value
  const themePrimary = getCssColorVar('--el-color-primary', 'var(--el-color-primary)')
  const dangerRed = getCssColorVar('--el-color-danger', '#E54545')
  const unit = t('systemDistChart.unit')

  const option = {
    color: [themePrimary, dangerRed],
    grid: {
      left: 100,
      right: 40,
    },
    yAxis: {
      data: names,
      triggerEvent: true,
      inverse: true,
      axisLabel: {
        interval: 0,
      },
    },
    series: [
      {
        name: t('subnetStatus.installedCount'),
        type: 'bar',
        data: installed,
        itemStyle: {
          color: themePrimary,
          borderRadius: 6,
        },
      },
      {
        name: t('subnetStatus.uninstalledCount'),
        type: 'bar',
        data: uninstalled,
        itemStyle: {
          color: dangerRed,
          borderRadius: 6,
        },
      },
    ],
    legend: {
      data: [t('subnetStatus.installedCount'), t('subnetStatus.uninstalledCount')],
      bottom: 0,
    },
  }

  return getAssignRankOption(option, { unit })
}

function renderChart() {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption(), true)
  bindChartClick()
}

function initChart() {
  chartInstance?.dispose()
  createChart()
  renderChart()
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance?.resize()
})

function loadData() {
  if (!props.hasPermission) {
    rawList.value = []
    return
  }
  isLoading.value = true
  subnetService
    .getSubnetStatusList({
      subnet_address: '',
      limit: 1000,
      page: 1,
      sort: 'uninstalled_clients',
      asc: false,
    })
    .then((res) => {
      rawList.value = res.list || []
    })
    .catch(() => {
      rawList.value = []
    })
    .finally(() => {
      isLoading.value = false
    })
}

watch(
  () => chartSeriesData.value,
  () => {
    if (!hasData.value) return
    if (!chartInstance) {
      nextTick(initChart)
    } else {
      renderChart()
    }
  },
  { deep: true }
)

/** 图例 / 系列名 / 单位等来自 t()，切换语言后须重新 setOption */
watch(locale, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
})

watch(
  () => props.hasPermission,
  (ok) => {
    if (ok) loadData()
    else rawList.value = []
  }
)

useEchartsThemeSync(renderChart)

onMounted(() => {
  loadData()
})

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance?.dispose()
  chartInstance = null
})

__expose({
  refreshData: loadData,
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('terminalDiscovery.subnetTopDualBarTitle'),
    class: "chart-card subnet-dual-chart-card"
  }, {
    titleAfter: _withCtx(() => [
      _createVNode(_component_el_tag, {
        size: "small",
        type: "primary"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("div", {
          ref_key: "chartRef",
          ref: chartRef,
          class: "chart-container"
        }, null, 512), [
          [_vShow, hasData.value]
        ]),
        _withDirectives(_createVNode(EmptyData, {
          height: "100%",
          "bg-color": "transparent"
        }, null, 512), [
          [_vShow, !hasData.value]
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})